/**
 * Shared status pill — used in BOTH the pipelines list table and the
 * detail page header. Single source of truth for the bundle-state +
 * warm-pool combined runtime experience:
 *
 *   bundleStatus='ready'    + warm  → Live      (green pulse)
 *   bundleStatus='ready'    + !warm → Sleeping  (gray + animated zz)
 *   bundleStatus='building'         → Building  (amber pulse)
 *   bundleStatus='failed'           → Degraded  (red, fix-me)
 *   bundleStatus=null|undef         → Sleeping  (mid-deploy / legacy)
 *
 * `variant` switches the CSS class prefix so the same logic renders
 * consistently in both contexts (table cell vs header subtitle), each
 * with its own typographic scale.
 *   variant="table"  → uses .pipelines-status-* (defined in
 *                       PipelinesPage.css)
 *   variant="header" → uses .pipeline-detail-status-* (defined in
 *                       PipelineDetailPage.css)
 *
 * Pure presentation — no data fetching, no state. Caller passes the
 * raw fields from the workflow row.
 */
import React from 'react';

const STATUS_TEXT = {
  live:     'Live',
  sleeping: 'Sleeping',
  building: 'Building',
  failed:   'Degraded',
};

export function resolveStatusKey({ bundleStatus, warmEnabled }) {
  if (bundleStatus === 'building') return 'building';
  if (bundleStatus === 'failed')   return 'failed';
  if (bundleStatus === 'ready' && warmEnabled) return 'live';
  return 'sleeping';
}

const PipelineStatus = ({ bundleStatus, warmEnabled, variant = 'table' }) => {
  const key = resolveStatusKey({ bundleStatus, warmEnabled });
  const text = STATUS_TEXT[key];
  const prefix = variant === 'header' ? 'pipeline-detail-status' : 'pipelines-status';

  return (
    <span className={`${prefix} ${prefix}-${key}`}>
      <span className={`${prefix}-dot`} />
      {text}
      {/* Animated zz only on Sleeping — both variants opt in via the
          shared .pipelines-status-zz class so the floating Z's look
          identical in table and header (font-size differs naturally
          via the parent variant's color/size). */}
      {key === 'sleeping' && (
        <span className="pipelines-status-zz" aria-hidden="true">
          <span className="pipelines-zz pipelines-zz-1">z</span>
          <span className="pipelines-zz pipelines-zz-2">z</span>
        </span>
      )}
    </span>
  );
};

export default PipelineStatus;
